import type { StateCreator } from "zustand"
import { create } from "zustand"
import { subscribeWithSelector } from "zustand/middleware"

import { withDevtools } from "../utils/auth/withDevtools"

import type {
  CurrentUser,
  CurrentUserGetFailure,
  GetCurrentUserQuery,
  GetCurrentUserQueryVariables,
} from "@/graphql/codegen/graphql"
import { GetCurrentUserDocument } from "@/graphql/codegen/graphql"
import { cachedQueryClient } from "@/providers/GraphqlRouterProvider"

interface CurrentUserStore {
  currentUser: CurrentUser | null
  fetching: boolean
  error: string | null
  getCurrentUser: () => Promise<CurrentUser | null>
}

const storeInitialiser: StateCreator<CurrentUserStore> = (set, get) => ({
  currentUser: null,
  fetching: false,
  error: null,
  getCurrentUser: async () => {
    if (!get().fetching) {
      set({ fetching: true })
      try {
        const { data, error } = await cachedQueryClient
          .query<GetCurrentUserQuery, GetCurrentUserQueryVariables>(GetCurrentUserDocument, {})
          .toPromise()

        if (error || data?.currentUser.get.__typename === "CurrentUserGetFailure") {
          const message = error?.message || (data?.currentUser.get as CurrentUserGetFailure).error.message
          set({ error: message, fetching: false })
        } else if (data?.currentUser.get.__typename === "CurrentUserGetSuccess") {
          set({
            currentUser: data?.currentUser.get.user,
            fetching: false,
            error: null,
          })
        } else {
          set({ currentUser: null, fetching: false, error: "Failed to fetch Current User" })
        }
      } catch (error) {
        set({ error: (error as Error).message, fetching: false })
      }
    }
    return get().currentUser
  },
})

export const useCurrentUserStore = create<CurrentUserStore>()(
  withDevtools(subscribeWithSelector(storeInitialiser), "CurrentUserStore")
)
